import { BeehaveReview } from '../types/beehaveReview';
import { AttachmentType } from './attachments';
import { RatingSubject } from './rating';
import { Task, TaskTypeClassification } from './task';

export enum WorkType {
    CREATE_FUNCTION = 1,
    UPDATE_FUNCTION = 2,
    DESCRIBE_FUNCTION = 3,
    REVIEW_TASK = 4,
    OPEN_TASK = 5,
    CREATE_REACT_COMPONENT = 6,
    UPDATE_REACT_COMPONENT = 7,
    CHECK_REUSABILITY = 8,
    CODING = 9,
    RESOLVE_CONFLICTS = 12,
    QUEST_REVIEW = 13,
    REVIEW_REQUEST_REJECTION = 16,
    PLANNING_QUESTIONS_ANSWERING = 17,
}

export enum WorkStatus {
    AVAILABLE = 1,
    UNAVAILABLE = 2,
    COMPLETE = 3,
    PENDING_PACKAGE = 4
}

export enum WorkReviewStatus {
    INADEQUATE = 1,
    REQUIRES_MODIFICATION = 2,
    ADEQUATE = 3
}

export enum WorkOutcome {
    REQUESTED_PACKAGE = 1,
    FEEDBACK = 2,
    SOLVED = 3,
    CANCELLED = 4,
    SKIPPED = 5,
    TASK_CANCELLED = 6
}

export enum ProjectContextType {
    QA = 1,
    SCOPE = 2,
    DOCUMENTATION = 3,
    ARCHITECTURE = 4
}

export const workMaxDurationMs = 10*60*60*1000;

export const isFollowupWorkItem = (workType: WorkType): boolean => {
    return [
        WorkType.RESOLVE_CONFLICTS,
        WorkType.QUEST_REVIEW
    ].indexOf(workType) > -1;
};

export const isCodingWorkItem = (workType: WorkType): boolean => {
    return workType === WorkType.CODING;
};

export const isResolveConflictsWorkItem = (workType: WorkType): boolean => {
    return workType === WorkType.RESOLVE_CONFLICTS;
};

export const isQuestReviewWorkItem = (workType: WorkType): boolean => {
    return workType === WorkType.QUEST_REVIEW;
};

export const isReviewRequestRejectionWorkItem = (workType: WorkType): boolean => {
    return workType === WorkType.REVIEW_REQUEST_REJECTION;
};

export const isPlanningQuestionsAnsweringWorkItem = (
    workType: WorkType
): boolean => {
    return workType === WorkType.PLANNING_QUESTIONS_ANSWERING;
};

/**
 * Shape of the planning Q&A surface served to the contributor. Mirrors
 * ``beehive-dashboards/src/types/planning.ts`` — the contributor's
 * answer submission hits the same backend endpoint.
 */
export type PlanningQuestionOption = {
    value: string;
    label: string;
};

export type PlanningQuestion = {
    type?: 'singleSelect' | 'multiSelect';
    question: string;
    options: PlanningQuestionOption[];
    questionPhase: 'product' | 'technical' | null;
};

export enum PlanningSessionStatus {
    INITIATED = 'initiated',
    PROCESSING = 'processing',
    AWAITING_ANSWERS = 'awaiting_answers',
    PLAN_READY = 'plan_ready',
    FINALIZED = 'finalized',
    FAILED = 'failed',
    EXPIRED = 'expired'
}

export type PlanningSessionData = {
    sessionId: string;
    status: PlanningSessionStatus;
    questions: PlanningQuestion[] | null;
    plan: string | null;
    turnCount: number;
    questId: string | null;
    clarityScore: number | null;
};

export type PlanningSubmitAnswerItem = {
    questionId: string;
    selectedOptionIds: string[];
    freeText: string | null;
};

export type TaskContext = {
    id: string;
    file: string;
    entity: string;
    potentialUse: string;
};

export type Attachment = {
    id: number;
    name: string;
    type: string;
    step: AttachmentType;
};

export type SuccessCriteria = {
    title: string;
    description: string;
    explanation: string;
};

export type WorkItem = {
    id: number;
    created: string;
    taskId: string;
    taskType?: TaskTypeClassification;
    title?: string;
    skills: [string];
    context?: TaskContext[];
    task: Task;
    status: WorkStatus;
    workType: WorkType;
    description: string;
    workInput: {[key: string]: any};
    projectId: number;
    projectName: string;
    projectDescription?: string;
    taskAttachments: Attachment[];
    projectAttachments: Attachment[];
    isSeniorWork?: boolean;
};

export type ProhibitedUsers = {
    prohibitedWorkerIds: string[];
    prohibitedWorkerNames: string[];
};

export type WorkRecord = {
    id: number;
    workId: number;
    work: WorkItem;
    startTimeEpochMs: number;
    durationSeconds: number;
    solutionFeedback: string;
    ratingSubjects: RatingSubject[];
    ratingAuthorizationCode: string;
    latestBeehaveReview: BeehaveReview;
};

export type QuestIterations = {
  [key: number]: string[];
};

export type TaskAnalysisRequestParams = {
    title: string;
    description: string;
    type?: string;
};
